import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Building2,
  Users,
  Mail,
  Trash2,
  Plus,
  Loader2,
  Send,
  Copy,
  Building,
  ShieldCheck,
  ScrollText,
} from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "school", label: "School" },
  { v: "college", label: "College" },
  { v: "university", label: "University" },
  { v: "coaching", label: "Coaching institute" },
  { v: "training", label: "Training academy" },
  { v: "enterprise", label: "Enterprise" },
];

const ROLES = [
  { v: "teacher", label: "Teacher" },
  { v: "hod", label: "HOD" },
  { v: "coordinator", label: "Coordinator" },
  { v: "admin", label: "Admin" },
];

export default function Institution() {
  const { user, checkAuth } = useAuth();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institutions/me");
      setInst(data);
    } catch (e) {
      setInst(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (loading) {
    return (
      <Layout title="Institution">
        <div className="grid place-items-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!inst) {
    return (
      <Layout title="Institution">
        <CreateInstitutionFlow
          onCreated={async () => {
            await checkAuth();
            await refresh();
          }}
        />
      </Layout>
    );
  }

  return (
    <Layout title={`Institution · ${inst.name}`}>
      <InstitutionDashboard
        institution={inst}
        currentUser={user}
        onUpdated={async () => {
          await checkAuth();
          await refresh();
        }}
      />
    </Layout>
  );
}

function CreateInstitutionFlow({ onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    type: "school",
    country: "",
    city: "",
    website: "",
    primary_color: "#7c3aed",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter institution name");
      return;
    }
    setBusy(true);
    try {
      await api.post("/institutions", form);
      toast.success("Institution created — you're now the admin");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create institution");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="create-institution-flow">
      <div className="tb-card p-8">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">
          <Building2 className="h-3.5 w-3.5" /> Institution onboarding
        </div>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium">
          Bring your school onboard.
        </h2>
        <p className="mt-2 text-muted-foreground text-sm">
          Set up your school, college, university or coaching center in 30 seconds. You can invite teachers, organize departments, and customize branding.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <Label>Institution name</Label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Sunshine High School"
              className="mt-1.5 h-11 rounded-xl"
              data-testid="inst-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => update("type", v)}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="inst-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Country</Label>
              <Input
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                placeholder="India"
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>City</Label>
              <Input
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="Bengaluru"
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label>Website (optional)</Label>
              <Input
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                placeholder="https://example.edu"
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
          </div>
          <Button
            onClick={submit}
            disabled={busy}
            className="w-full h-11 rounded-full"
            data-testid="inst-create-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Building2 className="h-4 w-4 mr-1.5" /> Create institution</>}
          </Button>
        </div>
      </div>

      <div className="tb-card p-8 bg-gradient-to-br from-violet-50 via-white to-emerald-50 dark:from-violet-500/10 dark:via-card dark:to-emerald-500/10">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-violet-700 dark:text-violet-300">
          <ShieldCheck className="h-3.5 w-3.5" /> What you get
        </div>
        <ul className="mt-6 space-y-4 text-sm">
          <Feat title="Teacher invitations" desc="Invite teachers by email with role-based permissions." />
          <Feat title="Departments" desc="Organize teachers by Math, Science, Languages, etc." />
          <Feat title="Roles" desc="Admin, HOD, Coordinator and Teacher access levels." />
          <Feat title="Audit logs" desc="See every administrative action across your institution." />
          <Feat title="White-label ready" desc="Custom logo and primary color — yours, not ours." />
          <Feat title="Multi-campus ready" desc="Architecture supports franchises and branches." />
        </ul>
      </div>
    </div>
  );
}

function Feat({ title, desc }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 h-5 w-5 rounded-full bg-violet-500 text-white text-[10px] grid place-items-center">✓</span>
      <div>
        <p className="font-medium text-sm">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </li>
  );
}

function InstitutionDashboard({ institution, currentUser, onUpdated }) {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = currentUser?.role === "admin";

  const refresh = async () => {
    setLoading(true);
    try {
      const reqs = [
        api.get("/institutions/me/stats"),
        api.get("/institutions/me/members"),
        api.get("/institutions/me/departments"),
      ];
      if (isAdmin) reqs.push(api.get("/institutions/me/invitations"));
      const results = await Promise.all(reqs);
      setStats(results[0].data);
      setMembers(results[1].data);
      setDepartments(results[2].data);
      if (isAdmin) setInvites(results[3].data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-500 to-emerald-400 grid place-items-center text-white">
            <Building className="h-6 w-6" />
          </div>
          <div>
            <h2 className="font-display text-2xl tracking-tight font-medium">{institution.name}</h2>
            <p className="text-sm text-muted-foreground capitalize">
              {institution.type} {institution.city ? `· ${institution.city}` : ""}
              {institution.country ? `, ${institution.country}` : ""}
              <span className="ml-2 tb-chip-lavender rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]">
                Plan: {institution.plan}
              </span>
            </p>
          </div>
        </div>
        {isAdmin && <BrandingDialog institution={institution} onUpdated={onUpdated} />}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="inst-stats">
        <Stat chip="tb-chip-lavender" label="Members" value={stats?.members || 0} />
        <Stat chip="tb-chip-mint" label="Departments" value={stats?.departments || 0} />
        <Stat chip="tb-chip-butter" label="Pending invites" value={stats?.pending_invites || 0} />
        <Stat chip="tb-chip-mint" label="Classes" value={stats?.classes || 0} />
        <Stat chip="tb-chip-lavender" label="AI generations" value={stats?.generations || 0} />
        <Stat chip="tb-chip-butter" label="Documents" value={stats?.documents || 0} />
      </div>

      <Tabs defaultValue="members">
        <TabsList className="rounded-full bg-muted p-1">
          <TabsTrigger value="members" className="rounded-full px-4" data-testid="tab-members">
            <Users className="h-3.5 w-3.5 mr-1.5" /> Members
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="invites" className="rounded-full px-4" data-testid="tab-invites">
              <Mail className="h-3.5 w-3.5 mr-1.5" /> Invitations
            </TabsTrigger>
          )}
          <TabsTrigger value="departments" className="rounded-full px-4" data-testid="tab-departments">
            <Building className="h-3.5 w-3.5 mr-1.5" /> Departments
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="audit" className="rounded-full px-4" data-testid="tab-audit">
              <ScrollText className="h-3.5 w-3.5 mr-1.5" /> Audit Log
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="members" className="mt-5">
          <MembersTab
            members={members}
            departments={departments}
            isAdmin={isAdmin}
            currentUser={currentUser}
            onChange={refresh}
            loading={loading}
          />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="invites" className="mt-5">
            <InvitesTab invites={invites} onChange={refresh} departments={departments} />
          </TabsContent>
        )}
        <TabsContent value="departments" className="mt-5">
          <DepartmentsTab departments={departments} isAdmin={isAdmin} onChange={refresh} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="audit" className="mt-5">
            <AuditLogTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit/logs?limit=200");
        setLogs(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!logs.length) return <p className="text-sm text-muted-foreground">No audit events yet.</p>;

  return (
    <div className="overflow-x-auto" data-testid="audit-log-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
            <th className="py-2 pr-4">Time</th>
            <th className="py-2 pr-4">Action</th>
            <th className="py-2 pr-4">User</th>
            <th className="py-2 pr-4">Target</th>
            <th className="py-2 pr-4">Metadata</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.log_id} className="border-b border-border align-top" data-testid={`audit-row-${log.log_id}`}>
              <td className="py-2 pr-4 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                {new Date(log.created_at).toLocaleString()}
              </td>
              <td className="py-2 pr-4">
                <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium tb-chip-lavender">
                  {log.action}
                </span>
              </td>
              <td className="py-2 pr-4 font-mono text-[11px]">{log.user_id?.slice(0, 18) || "—"}</td>
              <td className="py-2 pr-4 font-mono text-[11px]">{log.target?.slice(0, 18) || "—"}</td>
              <td className="py-2 pr-4 max-w-md">
                <pre className="text-[11px] whitespace-pre-wrap text-muted-foreground line-clamp-2">
                  {log.metadata && Object.keys(log.metadata).length > 0
                    ? JSON.stringify(log.metadata)
                    : "—"}
                </pre>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, chip }) {
  return (
    <div className="tb-card p-4">
      <p className={`inline-flex whitespace-nowrap text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 ${chip}`}>{label}</p>
      <p className="mt-2 font-display text-xl tracking-tight font-medium">{value}</p>
    </div>
  );
}

function MembersTab({ members, departments, isAdmin, currentUser, onChange, loading }) {
  const updateRole = async (userId, role) => {
    try {
      await api.patch(`/institutions/me/members/${userId}`, { role });
      toast.success("Role updated");
      onChange();
    } catch {
      toast.error("Update failed");
    }
  };

  const updateDept = async (userId, departmentId) => {
    try {
      await api.patch(`/institutions/me/members/${userId}`, { department_id: departmentId || null });
      toast.success("Department updated");
      onChange();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (userId) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api.delete(`/institutions/me/members/${userId}`);
      toast.success("Member removed");
      onChange();
    } catch {
      toast.error("Remove failed");
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!members.length) return <p className="text-sm text-muted-foreground">No members yet.</p>;

  return (
    <div className="overflow-x-auto" data-testid="members-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
            <th className="py-2 pr-4">Member</th>
            <th className="py-2 pr-4">Role</th>
            <th className="py-2 pr-4">Department</th>
            <th className="py-2 pr-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.user_id} className="border-b border-border" data-testid={`member-row-${m.user_id}`}>
              <td className="py-3 pr-4">
                <div>
                  <p className="font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </div>
              </td>
              <td className="py-3 pr-4">
                {isAdmin && m.user_id !== currentUser?.user_id ? (
                  <Select value={m.role} onValueChange={(v) => updateRole(m.user_id, v)}>
                    <SelectTrigger className="h-9 w-36 rounded-full text-xs" data-testid={`role-select-${m.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="capitalize">{m.role}</span>
                )}
              </td>
              <td className="py-3 pr-4">
                {isAdmin ? (
                  <Select
                    value={m.department_id || "_none"}
                    onValueChange={(v) => updateDept(m.user_id, v === "_none" ? null : v)}
                  >
                    <SelectTrigger className="h-9 w-44 rounded-full text-xs">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No department</SelectItem>
                      {departments.map((d) => (
                        <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  departments.find((d) => d.department_id === m.department_id)?.name || "—"
                )}
              </td>
              <td className="py-3 pr-4 text-right">
                {isAdmin && m.user_id !== currentUser?.user_id && (
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(m.user_id)} data-testid={`remove-member-${m.user_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvitesTab({ invites, onChange, departments }) {
  return (
    <div className="space-y-4" data-testid="invites-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Send invitations and copy the secure link to share.</p>
        <NewInviteDialog departments={departments} onCreated={onChange} />
      </div>
      {!invites.length && <p className="text-sm text-muted-foreground">No invitations yet.</p>}
      <div className="space-y-2">
        {invites.map((iv) => {
          const link = `${window.location.origin}/accept-invitation?token=${iv.token}`;
          return (
            <div key={iv.invitation_id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4" data-testid={`invite-row-${iv.invitation_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{iv.email}</p>
                <p className="text-[11px] text-muted-foreground capitalize">
                  {iv.role} · status: {iv.status}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() => {
                  navigator.clipboard.writeText(link);
                  toast.success("Invitation link copied");
                }}
                data-testid={`copy-invite-${iv.invitation_id}`}
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
              </Button>
              {iv.status === "pending" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={async () => {
                    try {
                      await api.delete(`/institutions/me/invitations/${iv.invitation_id}`);
                      onChange();
                    } catch {
                      toast.error("Failed to revoke");
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewInviteDialog({ onCreated, departments }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", role: "teacher", department_id: "" });

  const create = async () => {
    if (!form.email) return;
    setBusy(true);
    try {
      await api.post("/institutions/me/invitations", {
        email: form.email,
        role: form.role,
        department_id: form.department_id || null,
      });
      setOpen(false);
      setForm({ email: "", role: "teacher", department_id: "" });
      onCreated();
      toast.success("Invitation created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full" data-testid="new-invite-button">
          <Send className="h-3.5 w-3.5 mr-1.5" /> Invite teacher
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New invitation</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="teacher@example.edu"
              className="mt-1.5 h-11 rounded-xl"
              data-testid="invite-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Department</Label>
              <Select value={form.department_id || "_none"} onValueChange={(v) => setForm({ ...form, department_id: v === "_none" ? "" : v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No department</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy} data-testid="invite-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DepartmentsTab({ departments, isAdmin, onChange }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/institutions/me/departments", { name });
      setName("");
      onChange();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      await api.delete(`/institutions/me/departments/${id}`);
      onChange();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="departments-tab">
      {isAdmin && (
        <div className="flex gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mathematics"
            className="h-11 rounded-full"
            data-testid="new-dept-name"
          />
          <Button onClick={create} disabled={busy} className="h-11 rounded-full" data-testid="new-dept-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add</>}
          </Button>
        </div>
      )}
      {!departments.length && <p className="text-sm text-muted-foreground">No departments yet.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {departments.map((d) => (
          <div key={d.department_id} className="tb-card p-4 flex items-center justify-between" data-testid={`dept-${d.department_id}`}>
            <p className="font-medium">{d.name}</p>
            {isAdmin && (
              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(d.department_id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandingDialog({ institution, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: institution.name || "",
    type: institution.type || "school",
    logo_url: institution.logo_url || "",
    primary_color: institution.primary_color || "#7c3aed",
    website: institution.website || "",
    country: institution.country || "",
    city: institution.city || "",
  });

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch("/institutions/me", form);
      toast.success("Updated");
      setOpen(false);
      onUpdated?.();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="branding-trigger">
          Edit institution
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit institution</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Primary color</Label>
              <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>
          <div>
            <Label>Logo URL</Label>
            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="branding-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
